import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CustomDrawer from '@/atoms/custom-drawer';
import DynamicLoader from '@/atoms/dynamic-loader';
import PageHeader from '@/atoms/page-header';
import PropertyAdvancedFilter from '@/molecules/property-advanced-filter';
import { useRentalForm } from '@core/offices/actions/properties/useRentalForm';
import { OfficePaginator } from '@graphql/hooks-and-types';

import RentalResult from '../rental-result';

const RentalSearch = dynamic(() => import('@/organisms/rental-search'), {
  loading: () => (
    <div className="w-screen h-9 flex justify-center items-center">
      <DynamicLoader size="medium" />
    </div>
  ),
});

const Rental: React.FC<{
  filteredPropertiesPaginator: OfficePaginator;
  locationTypes: string[];
}> = ({ filteredPropertiesPaginator, locationTypes }) => {
  const { t } = useTranslation();
  const { error, loading: loadingData, isRefetching, fetchMore, handleSubmit } = useRentalForm();
  const [isMapOpened, setIsMapOpened] = useState(true);

  const [isFiltersDrawerOpened, setIsFiltersDrawerOpened] = useState(false);

  const toggleFiltersDrawer = useCallback(() => {
    setIsFiltersDrawerOpened(!isFiltersDrawerOpened);
  }, [isFiltersDrawerOpened]);

  const toggleMap = useCallback(() => {
    setIsMapOpened(!isMapOpened);
  }, [isMapOpened]);

  const closeFilterDrawer = useCallback(() => {
    setIsFiltersDrawerOpened(false);
  }, []);

  if (error) {
    return <div>{error.message}</div>;
  }

  return (
    <div className="relative w-full" style={{ height: window.screen.availHeight - 140 }}>
      <CustomDrawer title={t('rentals.filterBy')} onClose={toggleFiltersDrawer} open={isFiltersDrawerOpened}>
        <PropertyAdvancedFilter
          locationTypes={locationTypes.map((item) => ({
            label: item,
            value: item,
          }))}
          handleSubmit={handleSubmit}
          onSubmit={closeFilterDrawer}
        />
      </CustomDrawer>
      <div className={`px-2 ${isMapOpened && 'pt-6 md:pt-0'}`}>
        <div className={`mt-5 md:mt-6 mb-5 md:mb-7 ${isMapOpened ? 'hidden md:block' : ''}`}>
          <PageHeader title={t('rentals.title')} description={t('rentals.pageDescription')} />
        </div>
        <RentalSearch
          handleSubmit={handleSubmit}
          locationTypes={locationTypes.map((item) => ({
            label: item,
            value: item,
          }))}
          toggleFiltersDrawer={toggleFiltersDrawer}
          toggleMap={toggleMap}
        />
      </div>
      <RentalResult
        loading={loadingData && !isRefetching}
        fetchMore={fetchMore}
        filteredPropertiesPaginator={filteredPropertiesPaginator}
        isMapOpened={isMapOpened}
      />
    </div>
  );
};

export default Rental;
